import React, { useState } from 'react';

import {
  Badge,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import {
  ExitToApp as ExitToAppIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { formatPrice } from '../../../shared/utils/format-price.util';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { HeaderSummary, HeaderSummaryItem } from '../../../shared/components/HeaderSummary.component';

// ── Types ────────────────────────────────────────────────────────────────────

export interface OrderTableHeader {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

export interface OrderItemRow {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  noLabel: boolean;
  quantity: number;
  unitPrice: number;
}

export interface OrderRowDetails {
  headerSummary: HeaderSummaryItem[];
  items: OrderItemRow[];
  deliveryCost?: number;
}

export interface OrderTableRow {
  id: string;
  href: string;
  cells: { key: string; content: React.ReactNode }[];
  details: OrderRowDetails;
}

interface OrderTableWithDetailsProps {
  headers: OrderTableHeader[];
  orders: OrderTableRow[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  isLoading: boolean;
  maxHeight?: string;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const useRowStyles = makeStyles({
  root: (props: { isOpen: boolean }) => ({
    '& > .MuiTableCell-root': props.isOpen
      ? { padding: '0.5rem', borderBottom: '1px solid rgba(224, 224, 224, 1)' }
      : { padding: '0 0.5rem', borderBottom: 0 },
  }),
  orderRow: (props: { isOpen: boolean }) => ({
    '& > .MuiTableRow-root': {
      backgroundColor: props.isOpen ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
    },
    '& > .MuiTableCell-root': {
      borderBottom: props.isOpen ? 0 : '1px solid rgba(224, 224, 224, 1)',
    },
  }),
  collapse: {
    '& .MuiPaper-root': {
      padding: '1rem',
      '& > h4': { margin: 0 },
    },
  },
  itemsFooter: {
    '& .MuiTableCell-root': {
      borderBottom: 0,
      color: '#f44336',
    },
  },
});

// ── Sub-components ───────────────────────────────────────────────────────────

const ITEMS_TABLE_HEADERS = [
  { key: 'name', label: 'Produit' },
  { key: 'category', label: 'Rubrique' },
  { key: 'subCategory', label: 'Sous-rubrique' },
  { key: 'label', label: 'Étiquette', align: 'center' as const },
  { key: 'quantity', label: 'Quantité', align: 'right' as const },
  { key: 'price', label: 'Prix/u', align: 'right' as const },
  { key: 'total', label: 'Prix total', align: 'right' as const },
];

function OrderItemsSummaryTable({ items, deliveryCost = 0 }: { items: OrderItemRow[]; deliveryCost?: number }) {
  const classes = useRowStyles({ isOpen: false });

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {ITEMS_TABLE_HEADERS.map((header) => (
            <TableCell style={{ fontWeight: 'bold' }} key={header.key} align={header.align}>
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id}>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.category}</TableCell>
            <TableCell>{item.subcategory}</TableCell>
            <TableCell align="center">
              <Badge color={item.noLabel ? 'secondary' : 'primary'} variant="dot" />
            </TableCell>
            <TableCell align="right">{item.quantity}</TableCell>
            <TableCell align="right">{formatPrice(item.unitPrice)}</TableCell>
            <TableCell align="right">{formatPrice(item.unitPrice * item.quantity)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className={classes.itemsFooter}>
        <TableRow>
          <TableCell colSpan={6} />
          <TableCell align="right">
            {deliveryCost > 0 ? `Frais de livraison - ${formatPrice(deliveryCost)}` : 'Aucun frais de livraison'}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

function OrderSummaryCollapse({
  isOpen,
  colSpan,
  details,
}: {
  isOpen: boolean;
  colSpan: number;
  details: OrderRowDetails;
}) {
  const classes = useRowStyles({ isOpen });

  return (
    <TableRow className={classes.root} tabIndex={-1}>
      <TableCell colSpan={colSpan}>
        <Collapse className={classes.collapse} in={isOpen} timeout="auto" unmountOnExit>
          <HeaderSummary items={details.headerSummary} />
          <OrderItemsSummaryTable items={details.items} deliveryCost={details.deliveryCost} />
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

function OrderRow({ row, colSpan, headers }: { row: OrderTableRow; colSpan: number; headers: OrderTableHeader[] }) {
  const [open, setOpen] = useState(false);
  const classes = useRowStyles({ isOpen: open });

  return (
    <>
      <TableRow className={classes.orderRow} hover={!open} tabIndex={-1}>
        <TableCell align="left" style={{ maxWidth: 50 }}>
          <IconButton size="small" aria-label="expand row" onClick={() => setOpen((prev) => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="center" style={{ maxWidth: 50 }}>
          <IconButton size="small" aria-label="open order" href={row.href}>
            <ExitToAppIcon />
          </IconButton>
        </TableCell>
        {row.cells.map((cell, index) => (
          <TableCell key={cell.key} align={headers[index]?.align} style={{ minWidth: headers[index]?.minWidth }}>
            {cell.content}
          </TableCell>
        ))}
      </TableRow>
      <OrderSummaryCollapse isOpen={open} colSpan={colSpan} details={row.details} />
    </>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function OrderTableWithDetails({
  headers,
  orders,
  totalCount,
  page,
  rowsPerPage,
  rowsPerPageOptions = [50, 100, 200],
  isLoading,
  maxHeight = '73.5svh',
  onPageChange,
  onRowsPerPageChange,
}: OrderTableWithDetailsProps) {
  const colSpan = headers.length + 2;

  return (
    <Paper>
      <TableContainer style={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ maxWidth: 50 }} />
              <TableCell align="center" style={{ maxWidth: 50 }} />
              {headers.map((header) => (
                <TableCell
                  key={header.key}
                  align={header.align}
                  style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={colSpan} />}
          {!isLoading && (
            <TableBody>
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan} style={{ textAlign: 'center' }}>
                    Aucune commande
                  </TableCell>
                </TableRow>
              )}
              {orders.map((row) => (
                <OrderRow key={row.id} row={row} colSpan={colSpan} headers={headers} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={rowsPerPageOptions}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
